import type { input } from 'zod/mini'
import { enum as enumz, maxLength, refine, strictObject, string } from 'zod/mini'
import { isValidPhoneNumber } from 'libphonenumber-js'
import { MOTIF_MAX_LENGTH } from 'commons/constants/motif'
import { isXpayType, PaymentTypes } from 'commons/constants/paymentTypes'
import { LYDIA_PAGE_AMOUNTS, MONEY_POT_AMOUNTS } from 'commons/constants/amount'
import { toNumber } from 'commons/utils/amountHelpers'

const requiresEmailForPayment = (paymentType: string) =>
	paymentType === PaymentTypes.CARD || isXpayType(paymentType as PaymentTypes)

export const createSchema = (
	getAmount?: () => string,
	getPaymentType?: () => string,
	requireName?: boolean | (() => boolean),
	requireMotif?: boolean | (() => boolean)
) => {
	const getRequireName =
		typeof requireName === 'function' ? requireName : () => requireName ?? false
	const getRequireMotif =
		typeof requireMotif === 'function' ? requireMotif : () => requireMotif ?? true
	return strictObject({
		amount: string('phone_form.amount_error_required').check(
			refine((val) => {
				const n = toNumber(val) || 0
				return n >= 1
			}, 'phone_form.amount_error'),
			refine((val) => {
				const n = toNumber(val) || 0
				return n <= 1000
			}, 'phone_form.amount_error_max')
		),
		paymentType: enumz(PaymentTypes, 'phone_form.payment_type_error'),
		motif: string().check(
			maxLength(MOTIF_MAX_LENGTH, 'phone_form.reason_error_max_length'),
			refine(
				(val) => {
					const shouldRequireMotif = getRequireMotif()
					// If motif validation is disabled, always pass
					if (!shouldRequireMotif) {
						return true
					}
					const amount = toNumber(getAmount?.() ?? '0') || 0
					const motif = (val ?? '').trim()
					// If amount >= 25, motif is required
					if (amount >= 25) {
						if (motif.length === 0) return false
						return motif.length >= 3
					}
					// If amount < 25, motif is optional but must be >= 3 chars if provided
					return motif.length === 0 || motif.length >= 3
				},
				{
					error: (issue) => {
						const amount = toNumber(getAmount?.() ?? '0') || 0
						const motif = ((issue.input as string) ?? '').trim()
						// If amount >= 25 and motif is empty, it's required
						if (amount >= 25 && motif.length === 0) {
							return 'phone_form.reason_error_required'
						}
						// Otherwise it's a min length issue
						return 'phone_form.reason_error_min_length'
					}
				}
			)
		),
		phone: string().check(
			refine(
				(value) => {
					const paymentType = getPaymentType?.() ?? PaymentTypes.LYDIA
					// Phone is required only for Lydia payment
					if (paymentType === PaymentTypes.LYDIA) {
						if (!value) return false
						return isValidPhoneNumber(value)
					}
					// Phone is optional for card payment, but if provided must be valid
					return !value || isValidPhoneNumber(value)
				},
				{
					error: (value) => {
						const paymentType = getPaymentType?.() ?? PaymentTypes.LYDIA
						if (paymentType === PaymentTypes.LYDIA && !value) {
							return 'phone_form.phone_error_required'
						}
						return 'phone_form.phone_error_invalid'
					}
				}
			)
		),
		email: string().check(
			refine(
				(value) => {
					const paymentType = getPaymentType?.() ?? PaymentTypes.LYDIA
					// Email is required for card and xpay (Apple Pay / Google Pay)
					if (requiresEmailForPayment(paymentType)) {
						if (!value) return false
						return value.includes('@') && value.includes('.')
					}
					// Email is optional for Lydia payment, but if provided must be valid
					return !value || (value.includes('@') && value.includes('.'))
				},
				{
					error: (value) => {
						const paymentType = getPaymentType?.() ?? PaymentTypes.LYDIA
						if (requiresEmailForPayment(paymentType) && !value) {
							return 'phone_form.email_error_required'
						}
						return 'phone_form.email_error_invalid'
					}
				}
			)
		),
		name: string().check(
			refine(
				(value) => {
					const shouldRequireName = getRequireName()
					// Name is required only if requireName is true
					if (shouldRequireName) {
						return !!value && value.trim().length > 0
					}
					// Name is optional if requireName is false
					return true
				},
				{
					error: () => {
						return 'phone_form.name_error_required'
					}
				}
			)
		)
	})
}

export const schema = createSchema()

export const defaultLydiaPageValues: input<typeof schema> = {
	amount: LYDIA_PAGE_AMOUNTS[0],
	paymentType: PaymentTypes.CARD,
	motif: '',
	phone: '',
	email: '',
	name: ''
}

export const defaultMoneyPotValues: input<typeof schema> = {
	amount: MONEY_POT_AMOUNTS[1],
	paymentType: PaymentTypes.CARD,
	motif: '',
	phone: '',
	email: '',
	name: ''
}
